import React from 'react';
import { Button, createStyles, FormHelperText, Grid, Icon, makeStyles, Typography } from '@material-ui/core';
import { Action, Column, Options } from 'material-table';

import { SourceModel, sourceModelInitial } from '../../../../Models/SourceModel';
import { BasicConfirmDialog } from '../../../../shared/components/BasicConfirmDialog';
import { TableCommon } from '../../../../shared/components/TableCommon';
import { TableEmpty } from '../../../../shared/components/TableEmpty';
import { ClientContext } from '../../ClientContext';
import { EditAction } from '../../../../shared/enum/EditAction';
import { getTableColumn } from '../../../../shared/operators';
import { ClientErrorContext } from '../../ClientErrorContext';
import { ClientSourceModal } from '../components/ClientSourceModal';
import { ClientSourceContext } from '../ClientSourceContext';
import SourceService from '../../../../Services/SourceService';

const useStyles = makeStyles(() => createStyles({
    cardHeight: {
        overflow: 'hidden',
        maxHeight: '240px'
    },
    textButton: {
        paddingLeft: 0,
        '&:hover': {
            textDecorationLine: 'underline',
        },
    },
}));

export function ClientSource() {
    const classes = useStyles();

    const clientContext = React.useContext(ClientContext);
    const clientErrorContext = React.useContext(ClientErrorContext);

    const [actionFieldIndex, setActionFieldIndex] = React.useState<number>(-1);
    const [isInEditMode, setIsInEditMode] = React.useState(false);
    const [currentSource, setCurrentSource] = React.useState<SourceModel>(sourceModelInitial);

    const [isSourceModalOpen, setIsSourceModalOpen] = React.useState(false);
    const [isConfirmDeletionOfSourceModalOpen, setIsConfirmDeletionOfSourceModalOpen] = React.useState(false);

    const tableSourcesColumns: Column<SourceModel>[] = [
        getTableColumn('Nome', 'name', 'string', 'left', undefined, 'asc', true),
        {
            title: 'Cargo',
            type: 'string',
            align: 'left',
            sorting: true,
            field: 'position.description',
            render: rowData => rowData.position ? rowData.position.description : '-',
        },
        {
            title: 'Procedência',
            type: 'string',
            align: 'left',
            sorting: true,
            field: 'origin.name',
            render: rowData => rowData.origin ? rowData.origin.name : '-',
        }
    ];

    const tableSourcesOptions: Options<SourceModel> = {
        filtering: true,
        sorting: true,
        showTitle: false,
        toolbar: false,
        headerStyle: {
            fontSize: '14px',
            whiteSpace: 'nowrap'
        },
        pageSizeOptions: [5, 10, 15],
        pageSize: 5,
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tableSourcesActions: (Action<any> | ((rowData: SourceModel) => Action<any>))[] = [
        {
            icon: 'edit',
            iconProps: { style: { fontSize: '20px' } },
            tooltip: 'Editar fonte',
            onClick: (_, rowData) => { handleEditButton(rowData, rowData.tableData.id); }
        },
        {
            icon: 'delete_outline',
            iconProps: { style: { fontSize: '20px' } },
            tooltip: 'Apagar fonte',
            onClick: (_, rowData) => { setActionFieldIndex(rowData.tableData.id); setIsConfirmDeletionOfSourceModalOpen(true); },
        },
    ];

    const handleDownload = async () => {
        try {
            const response = await SourceService.sourceServiceDownload(clientContext.client.id);
            if (!response || !response.data) {
                throw new Error('Resposta inválida do servidor.');
            }

            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');

            a.href = url;
            a.download = `fontes_${clientContext.client.name}.xlsx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            window.URL.revokeObjectURL(url);

        } catch (error) {
            console.error('Erro ao baixar o arquivo:', error);
        }
    };

    const handleEditButton = (source: SourceModel, index: number) => {
        setIsInEditMode(true);
        setCurrentSource(source);
        setActionFieldIndex(index);
        setIsSourceModalOpen(true);
    };

    const handleDeleteButton = (index: number) => {
        const sources = [...clientContext.sources.filter((s) => s.editAction != EditAction.DELETE && s.endOfPeriod == null)];
        if (sources[index].editAction == EditAction.INSERT)
            sources.splice(index, 1);
        else
            sources[index].editAction = EditAction.DELETE;
        clientContext.setSources(sources);
        setIsConfirmDeletionOfSourceModalOpen(false);
        setActionFieldIndex(-1);
    };

    const getDownloadFile = () => {
        return (
            <Button
                onClick={handleDownload}
                size="large"
                color="primary"
                variant="outlined"
            >
                Exportar Fontes
            </Button>
        );
    };

    const getAddNewButton = () => {
        return (
            <Grid container justifyContent="space-between" alignItems="center">
                <Grid item>
                    <Button
                        onClick={() => setIsSourceModalOpen(true)}
                        className={classes.textButton}
                        startIcon={<Icon color='primary'>add</Icon>}>
                        Adicionar Fonte
                    </Button>
                </Grid>
                <Grid item>
                    {getDownloadFile()}
                </Grid>
            </Grid>
        );
    };

    const getSourceList = () => {
        return (
            <>
                {!clientContext.sources.length ||
                    clientContext.sources.filter((s) => s.editAction != EditAction.DELETE && s.endOfPeriod == null).length == 0
                    ? (
                        <Grid item xs={12} className={classes.cardHeight}>
                            <TableEmpty
                                displayText="Nenhuma fonte cadastrada"
                                cssProperties={{
                                    minHeight: '150px',
                                    errorBorder: (clientErrorContext.sourceNotDefinedError &&
                                        !(clientContext.sources
                                            && clientContext.sources.filter((s) => s.editAction != EditAction.DELETE
                                                && s.endOfPeriod == null).length > 0))
                                }} />
                            {(clientErrorContext.sourceNotDefinedError &&
                                !(clientContext.sources
                                    && clientContext.sources.filter((s) => s.editAction != EditAction.DELETE
                                        && s.endOfPeriod == null).length > 0)) &&
                                <FormHelperText error>
                                    Você precisa inserir pelo menos uma fonte para este cliente.
                                </FormHelperText>
                            }
                        </Grid>
                    ) : (
                        <Grid item xs={12}>
                            <TableCommon
                                tableData={clientContext.sources.filter((s) => s.editAction != EditAction.DELETE && s.endOfPeriod == null)}
                                tableColumns={tableSourcesColumns}
                                tableActions={tableSourcesActions}
                                extraOptions={tableSourcesOptions}
                                emptyDataSourceMessage='Nenhuma fonte cadastrada' />
                        </Grid>
                    )
                }
            </>
        );
    };

    return (
        <ClientSourceContext.Provider value={{
            actionFieldIndex, setActionFieldIndex,
            isInEditMode, setIsInEditMode,
            currentSource, setCurrentSource,
        }}>
            <Grid container direction="column" spacing={1}>
                <Grid item xs={12}>
                    <Typography variant="subtitle1">Fontes</Typography>
                </Grid>
                <Grid item xs={12}>
                    {getAddNewButton()}
                </Grid>
                {getSourceList()}
            </Grid>

            <ClientSourceModal showDialog={isSourceModalOpen} setShowDialog={setIsSourceModalOpen} />

            <BasicConfirmDialog
                title="Excluir fonte?"
                description="Ao excluir esta fonte, ela não ficará mais disponível para futuras classificações."
                handleFirstButton={() => handleDeleteButton(actionFieldIndex)}
                handleSecondButton={() => setIsConfirmDeletionOfSourceModalOpen(false)}
                textFirstButton="Excluir"
                textSecondButton="Voltar"
                reverseButtons={true}
                cssProperties={{ justifyButtons: 'flex-end' }}
                open={isConfirmDeletionOfSourceModalOpen} />
        </ClientSourceContext.Provider>
    );
}
